import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { LanguageAnalysisJSON } from '../lib/openai';

export interface LanguageAnalysisRecord {
  id: string;
  sermon_id: string | null;
  title: string;
  content: LanguageAnalysisJSON;
  created_at: string;
  updated_at: string;
}

interface CreateLanguageAnalysisData {
  sermon_id?: string | null;
  title: string;
  content: LanguageAnalysisJSON;
}

export function useLanguageAnalysis() {
  const [analyses, setAnalyses] = useState<LanguageAnalysisRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadAnalyses = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error: fetchError } = await supabase
        .from('language_analysis')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setAnalyses(data || []);
    } catch (err) {
      console.error('Error loading language analyses:', err);
      setError(err instanceof Error ? err.message : 'Failed to load language analyses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAnalyses();
  }, []);

  const saveAnalysis = async (data: CreateLanguageAnalysisData) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data: saved, error: saveError } = await supabase
      .from('language_analysis')
      .insert([{ ...data, user_id: user.id }])
      .select()
      .single();

    if (saveError) throw saveError;
    await loadAnalyses();
    return saved as LanguageAnalysisRecord;
  };

  const deleteAnalysis = async (id: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error: deleteError } = await supabase
      .from('language_analysis')
      .delete()
      .eq('id', id)
      .eq('user_id', user.id);

    if (deleteError) throw deleteError;
    await loadAnalyses();
  };

  return {
    analyses,
    loading,
    error,
    saveAnalysis,
    deleteAnalysis,
    refresh: loadAnalyses,
  };
}
